import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from './firebase';
import './DiscountDetail.css';

interface DiscountCode {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed_amount';
  discountValue: number;
  maxUses: number;
  usesCount: number;
  expirationDate: Timestamp;
  applicableServices?: string[];
  createdAt: Timestamp;
  active: 'active' | 'inactive';
}

interface Subcategory {
  id: string;
  name: string;
  categoryId: string;
  icon: string;
  order?: number;
  commissionRate?: number;
  active?: boolean;
}

interface Category {
  id: string;
  name: string;
  icon?: string;
  order?: number;
}

const DiscountDetail: React.FC = () => {
  const { discountId } = useParams<{ discountId: string }>();
  const navigate = useNavigate();
  
  const [discountCode, setDiscountCode] = useState<DiscountCode | null>(null);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  

  
  // Thêm state cho từng field riêng biệt
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [isEditingDiscountType, setIsEditingDiscountType] = useState(false);
  const [isEditingDiscountValue, setIsEditingDiscountValue] = useState(false);
  const [isEditingActive, setIsEditingActive] = useState(false);
  const [isEditingExpirationDate, setIsEditingExpirationDate] = useState(false);
  
  // Thêm state cho giá trị đang edit
  const [editedCode, setEditedCode] = useState('');
  const [editedDiscountType, setEditedDiscountType] = useState<'percentage' | 'fixed_amount'>('percentage');
  const [editedDiscountValue, setEditedDiscountValue] = useState('');
  const [editedActive, setEditedActive] = useState<'active' | 'inactive'>('active');
  const [editedExpirationDate, setEditedExpirationDate] = useState('');

  // Reset editing states when discountCode changes
  useEffect(() => {
    if (discountCode) {
      setEditedCode(discountCode.code || '');
      setEditedDiscountType(discountCode.discountType || 'percentage');
      setEditedDiscountValue(discountCode.discountValue?.toString() || '');
      setEditedActive(discountCode.active || 'active');
      
      // Convert Timestamp to local datetime-local string format
      if (discountCode.expirationDate) {
        const date = discountCode.expirationDate.toDate();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        setEditedExpirationDate(`${year}-${month}-${day}T${hours}:${minutes}`);
      }
    }
    setIsEditingCode(false);
    setIsEditingDiscountType(false);
    setIsEditingDiscountValue(false);
    setIsEditingActive(false);
    setIsEditingExpirationDate(false);
  }, [discountCode]);

  // Add stats state
  const [stats, setStats] = useState({
    totalUses: 0,
    activeServices: 0,
    remainingUses: 0,
    daysUntilExpiration: 0
  });

  const fetchDiscountCode = async () => {
    if (!discountId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const docRef = doc(db, 'discount_codes', discountId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setDiscountCode({
          id: docSnap.id,
          code: data.code || '',
          discountType: data.discountType || 'percentage',
          discountValue: data.discountValue || 0,
          maxUses: data.maxUses || 0,
          usesCount: data.usesCount || 0,
          expirationDate: data.expirationDate,
          applicableServices: data.applicableServices || [],
          createdAt: data.createdAt,
          active: data.active || 'active'
        });
      } else {
        setError('Không tìm thấy mã khuyến mãi');
      }
    } catch (error: any) {
      console.error('Lỗi khi tải mã khuyến mãi:', error);
      setError('Không thể tải thông tin mã khuyến mãi');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      let subcategoriesQuery;
      let querySnapshot;

      try {
        // Thử query với where clause để chỉ lấy những subcategories có active: true
        subcategoriesQuery = query(
          collection(db, 'subcategories'),
          where('active', '==', true),
          orderBy('order', 'asc')
        );
        querySnapshot = await getDocs(subcategoriesQuery);
      } catch (indexError) {
        // Nếu không có index cho where + orderBy, thử chỉ với where
        try {
          subcategoriesQuery = query(
            collection(db, 'subcategories'),
            where('active', '==', true)
          );
          querySnapshot = await getDocs(subcategoriesQuery);
        } catch (whereError) {
          // Nếu không có trường active, lấy tất cả và filter ở client
          try {
            subcategoriesQuery = query(
              collection(db, 'subcategories'),
              orderBy('order', 'asc')
            );
            querySnapshot = await getDocs(subcategoriesQuery);
          } catch (orderError) {
            subcategoriesQuery = collection(db, 'subcategories');
            querySnapshot = await getDocs(subcategoriesQuery);
          }
        }
      }

      const subcategoriesData: Subcategory[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Nếu đã query với where clause, tất cả đều là active
        // Nếu không, chỉ lấy những subcategories có active: true hoặc không có trường active
        if (data.active !== false) { // Mặc định là true nếu không có trường active
          subcategoriesData.push({
            id: doc.id,
            name: data.name || '',
            categoryId: data.categoryId || '',
            icon: data.icon || '',
            order: data.order || 0,
            commissionRate: data.commissionRate || 0,
            active: data.active !== false // Mặc định là true
          });
        }
      });

      if (subcategoriesData.length > 0) {
        subcategoriesData.sort((a, b) => {
          const aOrder = a.order || 0;
          const bOrder = b.order || 0;
          return aOrder - bOrder;
        });
      }

      setSubcategories(subcategoriesData);
      
    } catch (error: any) {
      console.error('Lỗi khi tải danh sách dịch vụ:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      let categoriesQuery;
      let querySnapshot;

      try {
        categoriesQuery = query(
          collection(db, 'services'),
          orderBy('order', 'asc')
        );
        querySnapshot = await getDocs(categoriesQuery);
      } catch (indexError) {
        try {
          categoriesQuery = collection(db, 'categories');
          querySnapshot = await getDocs(categoriesQuery);
        } catch (categoriesError) {
          categoriesQuery = collection(db, 'services');
          querySnapshot = await getDocs(categoriesQuery);
        }
      }

      const categoriesData: Category[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        categoriesData.push({
          id: doc.id,
          name: data.name || '',
          icon: data.icon || '',
          order: data.order || 0
        });
      });

      if (categoriesData.length > 0) {
        categoriesData.sort((a, b) => {
          const aOrder = a.order || 0;
          const bOrder = b.order || 0;
          return aOrder - bOrder;
        });
      }

      setCategories(categoriesData);
      
    } catch (error) {
      console.error('Lỗi khi tải danh sách ngành nghề:', error);
    }
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown Category';
  };

  const groupSubcategoriesByCategory = () => {
    const grouped: { [categoryId: string]: Subcategory[] } = {};
    
    // Tất cả subcategories trong state đã được filter là active
    subcategories.forEach(subcategory => {
      if (!grouped[subcategory.categoryId]) {
        grouped[subcategory.categoryId] = [];
      }
      grouped[subcategory.categoryId].push(subcategory);
    });
    
    return grouped;
  };

  const isServiceSelected = (serviceId: string): boolean => {
    return discountCode?.applicableServices?.includes(serviceId) || false;
  };

  const toggleService = async (serviceId: string) => {
    if (!discountCode || updating) return;
    
    try {
      setUpdating(true);
      
      const currentServices = discountCode.applicableServices || [];
      let newServices: string[];
      
      if (isServiceSelected(serviceId)) {
        // Remove service
        newServices = currentServices.filter(id => id !== serviceId);
      } else {
        // Add service
        newServices = [...currentServices, serviceId];
      }
      
      const docRef = doc(db, 'discount_codes', discountCode.id);
      await updateDoc(docRef, {
        applicableServices: newServices
      });
      
      setDiscountCode({
        ...discountCode,
        applicableServices: newServices
      });
      
    } catch (error: any) {
      console.error('Lỗi khi cập nhật dịch vụ áp dụng:', error);
      setError('Không thể cập nhật dịch vụ áp dụng');
    } finally {
      setUpdating(false);
    }
  };

  const selectAllServices = async () => {
    if (!discountCode || updating) return;
    
    try {
      setUpdating(true);
      
      // Tất cả subcategories trong state đã được filter là active
      const activeServiceIds = subcategories.map(sub => sub.id);
      
      const docRef = doc(db, 'discount_codes', discountCode.id);
      await updateDoc(docRef, {
        applicableServices: activeServiceIds
      });
      
      setDiscountCode({
        ...discountCode,
        applicableServices: activeServiceIds
      });
      
    } catch (error: any) {
      console.error('Lỗi khi chọn tất cả dịch vụ:', error);
      setError('Không thể chọn tất cả dịch vụ');
    } finally {
      setUpdating(false);
    }
  };



  const handleUpdate = async (field: 'code' | 'discountType' | 'discountValue' | 'active' | 'expirationDate') => {
    if (!discountCode || updating) return;

    try {
      setUpdating(true);
      const docRef = doc(db, 'discount_codes', discountCode.id);
      
      let updateData: any = {};
      let newValue: any;

      switch (field) {
        case 'code':
          if (!editedCode.trim()) {
            alert('Mã khuyến mãi không thể để trống');
            return;
          }
          newValue = editedCode.trim();
          updateData.code = newValue;
          break;
        case 'discountType':
          newValue = editedDiscountType;
          updateData.discountType = newValue;
          break;
        case 'discountValue':
          const value = parseFloat(editedDiscountValue);
          if (isNaN(value) || value < 0) {
            alert('Giá trị giảm không hợp lệ');
            return;
          }
          newValue = value;
          updateData.discountValue = newValue;
          break;
        case 'active':
          newValue = editedActive;
          updateData.active = newValue;
          break;
        case 'expirationDate':
          const newDate = new Date(editedExpirationDate);
          if (isNaN(newDate.getTime())) {
            alert('Ngày không hợp lệ');
            return;
          }
          newValue = Timestamp.fromDate(newDate);
          updateData.expirationDate = newValue;
          break;
      }

      await updateDoc(docRef, updateData);
      
      // Update local state
      setDiscountCode(prev => prev ? { ...prev, ...updateData } : null);

      // Reset edit mode
      switch (field) {
        case 'code':
          setIsEditingCode(false);
          break;
        case 'discountType':
          setIsEditingDiscountType(false);
          break;
        case 'discountValue':
          setIsEditingDiscountValue(false);
          break;
        case 'active':
          setIsEditingActive(false);
          break;
        case 'expirationDate':
          setIsEditingExpirationDate(false);
          break;
      }
      
    } catch (error: any) {
      console.error('Lỗi khi cập nhật:', error);
      alert('Không thể cập nhật thông tin. Vui lòng thử lại.');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = (field: 'code' | 'discountType' | 'discountValue' | 'active' | 'expirationDate') => {
    if (!discountCode) return;

    switch (field) {
      case 'code':
        setEditedCode(discountCode.code || '');
        setIsEditingCode(false);
        break;
      case 'discountType':
        setEditedDiscountType(discountCode.discountType || 'percentage');
        setIsEditingDiscountType(false);
        break;
      case 'discountValue':
        setEditedDiscountValue(discountCode.discountValue?.toString() || '');
        setIsEditingDiscountValue(false);
        break;
      case 'active':
        setEditedActive(discountCode.active || 'active');
        setIsEditingActive(false);
        break;
      case 'expirationDate':
        if (discountCode.expirationDate) {
          const date = discountCode.expirationDate.toDate();
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          setEditedExpirationDate(`${year}-${month}-${day}T${hours}:${minutes}`);
        }
        setIsEditingExpirationDate(false);
        break;
    }
  };

  const clearAllServices = async () => {
    if (!discountCode || updating) return;
    
    try {
      setUpdating(true);
      
      const docRef = doc(db, 'discount_codes', discountCode.id);
      await updateDoc(docRef, {
        applicableServices: []
      });
      
      setDiscountCode({
        ...discountCode,
        applicableServices: []
      });
      
    } catch (error: any) {
      console.error('Lỗi khi xóa tất cả dịch vụ:', error);
      setError('Không thể xóa tất cả dịch vụ');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (timestamp: Timestamp): string => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDiscountValue = (value: number, type: string): string => {
    if (type === 'percentage') {
      return `${value}%`;
    } else {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(value);
    }
  };

  const calculateStats = (discountData: DiscountCode) => {
    const now = new Date();
    const expirationDate = discountData.expirationDate.toDate();
    const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    // Tính số lượng dịch vụ active được chọn (tất cả subcategories trong state đã là active)
    const activeSelectedServices = discountData.applicableServices?.filter(serviceId => {
      const service = subcategories.find(sub => sub.id === serviceId);
      return service; // Tất cả subcategories trong state đã được filter là active
    }) || [];

    setStats({
      totalUses: discountData.usesCount,
      activeServices: activeSelectedServices.length,
      remainingUses: Math.max(0, discountData.maxUses - discountData.usesCount),
      daysUntilExpiration: Math.max(0, daysUntilExpiration)
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchCategories();
      await fetchSubcategories();
      await fetchDiscountCode();
    };
    fetchData();
  }, [discountId]);

  useEffect(() => {
    if (discountCode) {
      calculateStats(discountCode);
    }
  }, [discountCode, subcategories]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Đang tải...</div>
      </div>
    );
  }

  if (error || !discountCode) {
    return (
      <div className="error-container">
        <div className="error-message">{error || 'Không tìm thấy mã khuyến mãi'}</div>
        <button className="back-button" onClick={() => navigate('/manage-discount-codes')}>
          Quay lại
        </button>
      </div>
    );
  }

  return (
    <div className="discount-detail-container">
      {/* Header Section */}
      <div className="detail-header">
        <button className="back-button" onClick={() => navigate('/manage-discount-codes')}>
          ← Quay lại
        </button>
        <h1>Chi tiết mã khuyến mãi</h1>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Tổng lượt sử dụng</h3>
          <p className="stat-value">{stats.totalUses}</p>
        </div>
        <div className="stat-card">
          <h3>Dịch vụ áp dụng</h3>
          <p className="stat-value">{stats.activeServices}</p>
        </div>
        <div className="stat-card">
          <h3>Lượt còn lại</h3>
          <p className="stat-value">{stats.remainingUses}</p>
        </div>
        <div className="stat-card">
          <h3>Ngày còn lại</h3>
          <p className="stat-value">{stats.daysUntilExpiration}</p>
        </div>
      </div>

      {/* Main Info Section */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Thông tin cơ bản</h2>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <label>Mã khuyến mãi:</label>
            {isEditingCode ? (
              <div className="edit-controls">
                <input
                  type="text"
                  value={editedCode}
                  onChange={(e) => setEditedCode(e.target.value)}
                  className="edit-input"
                  disabled={updating}
                />
                <div className="edit-buttons">
                  <button
                    onClick={() => handleUpdate('code')}
                    disabled={updating || editedCode.trim() === ''}
                    className="save-btn"
                  >
                    {updating ? 'Đang lưu...' : 'Lưu'}
                  </button>
                  <button
                    onClick={() => handleCancel('code')}
                    disabled={updating}
                    className="cancel-btn"
                  >
                    Hủy
                  </button>
                </div>
              </div>
            ) : (
              <div className="info-value-container">
                <span className="info-value">{discountCode.code}</span>
                <button
                  onClick={() => setIsEditingCode(true)}
                  className="edit-btn"
                  title="Chỉnh sửa mã khuyến mãi"
                >
                  ✎
                </button>
              </div>
            )}
          </div>
          <div className="info-item">
            <label>Loại giảm giá:</label>
            {isEditingDiscountType ? (
              <div className="edit-controls">
                <select
                  value={editedDiscountType}
                  onChange={(e) => setEditedDiscountType(e.target.value as 'percentage' | 'fixed_amount')}
                  className="edit-select"
                  disabled={updating}
                >
                  <option value="percentage">Phần trăm (%)</option>
                  <option value="fixed_amount">Số tiền cố định (VND)</option>
                </select>
                <div className="edit-buttons">
                  <button
                    onClick={() => handleUpdate('discountType')}
                    disabled={updating}
                    className="save-btn"
                  >
                    {updating ? 'Đang lưu...' : 'Lưu'}
                  </button>
                  <button
                    onClick={() => handleCancel('discountType')}
                    disabled={updating}
                    className="cancel-btn"
                  >
                    Hủy
                  </button>
                </div>
              </div>
            ) : (
              <div className="info-value-container">
                <span className="info-value">
                  {discountCode.discountType === 'percentage' ? 'Phần trăm (%)' : 'Số tiền cố định (VND)'}
                </span>
                <button
                  onClick={() => setIsEditingDiscountType(true)}
                  className="edit-btn"
                  title="Chỉnh sửa loại giảm giá"
                >
                  ✎
                </button>
              </div>
            )}
          </div>
          <div className="info-item">
            <label>Giá trị giảm:</label>
            {isEditingDiscountValue ? (
              <div className="edit-controls">
                <input
                  type="number"
                  value={editedDiscountValue}
                  onChange={(e) => setEditedDiscountValue(e.target.value)}
                  className="edit-input"
                  disabled={updating}
                  min="0"
                  step={discountCode.discountType === 'percentage' ? '0.1' : '1000'}
                />
                <div className="edit-buttons">
                  <button
                    onClick={() => handleUpdate('discountValue')}
                    disabled={updating || editedDiscountValue.trim() === ''}
                    className="save-btn"
                  >
                    {updating ? 'Đang lưu...' : 'Lưu'}
                  </button>
                  <button
                    onClick={() => handleCancel('discountValue')}
                    disabled={updating}
                    className="cancel-btn"
                  >
                    Hủy
                  </button>
                </div>
              </div>
            ) : (
              <div className="info-value-container">
                <span className="info-value">
                  {formatDiscountValue(discountCode.discountValue, discountCode.discountType)}
                </span>
                <button
                  onClick={() => setIsEditingDiscountValue(true)}
                  className="edit-btn"
                  title="Chỉnh sửa giá trị giảm"
                >
                  ✎
                </button>
              </div>
            )}
          </div>
          <div className="info-item">
            <label>Số lần tối đa:</label>
            <span className="info-value">{discountCode.maxUses}</span>
          </div>
          <div className="info-item">
            <label>Ngày hết hạn:</label>
            {isEditingExpirationDate ? (
              <div className="edit-controls">
                <input
                  type="datetime-local"
                  value={editedExpirationDate}
                  onChange={(e) => setEditedExpirationDate(e.target.value)}
                  className="edit-input"
                  disabled={updating}
                />
                <div className="edit-buttons">
                  <button
                    onClick={() => handleUpdate('expirationDate')}
                    disabled={updating || editedExpirationDate.trim() === ''}
                    className="save-btn"
                  >
                    {updating ? 'Đang lưu...' : 'Lưu'}
                  </button>
                  <button
                    onClick={() => handleCancel('expirationDate')}
                    disabled={updating}
                    className="cancel-btn"
                  >
                    Hủy
                  </button>
                </div>
              </div>
            ) : (
              <div className="info-value-container">
                <span className="info-value">
                  {formatDate(discountCode.expirationDate)}
                </span>
                <button
                  onClick={() => setIsEditingExpirationDate(true)}
                  className="edit-btn"
                  title="Chỉnh sửa ngày hết hạn"
                >
                  ✎
                </button>
              </div>
            )}
          </div>
          <div className="info-item">
            <label>Trạng thái:</label>
            {isEditingActive ? (
              <div className="edit-controls">
                <select
                  value={editedActive}
                  onChange={(e) => setEditedActive(e.target.value as 'active' | 'inactive')}
                  className="edit-select"
                  disabled={updating}
                >
                  <option value="active">Đang hoạt động</option>
                  <option value="inactive">Không hoạt động</option>
                </select>
                <div className="edit-buttons">
                  <button
                    onClick={() => handleUpdate('active')}
                    disabled={updating}
                    className="save-btn"
                  >
                    {updating ? 'Đang lưu...' : 'Lưu'}
                  </button>
                  <button
                    onClick={() => handleCancel('active')}
                    disabled={updating}
                    className="cancel-btn"
                  >
                    Hủy
                  </button>
                </div>
              </div>
            ) : (
              <div className="info-value-container">
                <span className={`status-badge ${discountCode.active}`}>
                  {discountCode.active === 'active' ? 'Đang hoạt động' : 'Không hoạt động'}
                </span>
                <button
                  onClick={() => setIsEditingActive(true)}
                  className="edit-btn"
                  title="Chỉnh sửa trạng thái"
                >
                  ✎
                </button>
              </div>
            )}
          </div>
          <div className="info-item">
            <label>Ngày tạo:</label>
            <span className="info-value">{formatDate(discountCode.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Dịch vụ áp dụng</h2>
          <div className="section-actions">
            <button className="action-button" onClick={selectAllServices}>
              Chọn tất cả
            </button>
            <button className="action-button secondary" onClick={clearAllServices}>
              Bỏ chọn tất cả
            </button>
          </div>
        </div>
        <div className="services-grid">
          {Object.entries(groupSubcategoriesByCategory()).map(([categoryId, services]) => (
            <div key={categoryId} className="category-group">
              <h3 className="category-name">{getCategoryName(categoryId)}</h3>
              <div className="services-list">
                {services.map((service) => (
                  <div
                    key={service.id}
                    className={`service-item ${isServiceSelected(service.id) ? 'selected' : ''}`}
                    onClick={() => toggleService(service.id)}
                  >
                    <span className="service-name">{service.name}</span>
                    <span className="service-status">
                      {isServiceSelected(service.id) ? '✓' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DiscountDetail; 